import { FC, PropsWithChildren, useEffect, useMemo, useState } from 'react';

import OrganizationManager from '@gasbi/shared/common/utils/organizationManager';
import { NotAuthorizedView } from '@gasbi/shared/web/accessPages/NotAuthorizedView';
import { appEventBus } from '@gasbi/shared/web/utils/appEventBus';
import { AppEvents } from '@gasbi/shared/web/utils/appEventBus/eventMap';

import { useLazyGetCurrentUserQuery } from '@/features/auth/authApi';
import { AppAuthProvider } from '@/providers/AppAuthProvider';
import http from '@/utils/http';

/**
 * Cookie (BFF) session bootstrap (GSB-7612): one GET /api/user behind the HttpOnly cookie.
 * A 401 is handled by the http interceptor (navigation to /api/login); a 403 means the
 * identity signed in fine but has no access to BMS (no organizations). Then one
 * GET /api/session/organization (GSB-8274, PO 2026-10-25): the organization the user works in
 * lives in the session, the landing opens it. Without the endpoint (404 while the BMS API of
 * GSB-8276 is not deployed) or without an organization the landing decides as before.
 * Until the user is known, GlobalLoadingScreen covers the page.
 */
export const AuthInitProvider: FC<PropsWithChildren> = ({ children }) => {
  const [fetchCurrentUser, { data: user, error }] =
    useLazyGetCurrentUserQuery();
  const [isOrganizationReady, setIsOrganizationReady] = useState(false);

  const isForbidden =
    (error as { status?: number } | undefined)?.status === 403;

  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  useEffect(() => {
    if (!user) return;

    const onSettled = () => setIsOrganizationReady(true);
    OrganizationManager.initialize({ http }).then(onSettled, onSettled);
  }, [user]);

  useEffect(() => {
    if (isForbidden) {
      appEventBus.emit(AppEvents.LoadingStateEvent, false);
    }
  }, [isForbidden]);

  const value = useMemo(
    () =>
      user && {
        user,
        logout: () => {
          window.location.assign('/api/logout?returnUrl=/');
        },
      },
    [user],
  );

  if (isForbidden) {
    return <NotAuthorizedView />;
  }

  if (!value || !isOrganizationReady) {
    return null;
  }

  return <AppAuthProvider value={value}>{children}</AppAuthProvider>;
};
